import React, { useRef, useState } from 'react';
import { Record as RaRecord, useNotify, useRedirect, useTranslate } from 'react-admin';
import { Button, CircularProgress, IconButton, Link, TextField } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { updateResource, uploadFile } from '../../utils/requests';

const UPLOAD_TARGETS = {
  actionPlan: {
    documentType: 'actionPlan',
    bucketFolder: 'nutrition-advice/personal-nutrition-action-plan',
    labelKey: 'nutritionAdvice.consultation.uploadActionPlanLabel',
  },
  sessionDocumentation: {
    documentType: 'sessionDocumentation',
    bucketFolder: 'nutrition-advice/session-documentation',
    labelKey: 'nutritionAdvice.consultation.uploadSessionDocumentationLabel',
  },
} as const;

const FILE_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/gif,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const toDateInputValue = (value?: string): string => {
  if (!value) {
    return new Date().toISOString().split('T')[0];
  }

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().split('T')[0];
  }

  return value.includes('T') ? value.split('T')[0] : value;
};

const formatDateForHeader = (stepData: any): string => {
  const { consultationData, bookingData } = stepData || {};

  if (consultationData?.consultationDate) {
    const parsed = new Date(consultationData.consultationDate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('de-CH');
    }
    return consultationData.consultationDate;
  }

  if (consultationData?.completedAt) {
    const parsed = new Date(consultationData.completedAt);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('de-CH');
    }
  }

  return bookingData?.bookedConsultationDate || '';
};

interface ConsultationStepSectionProps {
  stepData: any;
  stepNumber: number;
  record: RaRecord;
  getFileUrl: (stepData: any, documentType: string) => string;
  readOnly?: boolean;
}

const ConsultationStepSection = ({
  stepData,
  stepNumber,
  record,
  getFileUrl,
  readOnly = false,
}: ConsultationStepSectionProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const isCompleted = readOnly || stepData.status === 'completed';
  const isActive = !readOnly && stepData.status === 'active';

  const existingActionPlanUrl = getFileUrl(stepData, UPLOAD_TARGETS.actionPlan.documentType);
  const existingSessionDocumentationUrl = getFileUrl(
    stepData,
    UPLOAD_TARGETS.sessionDocumentation.documentType,
  );

  const [isExpanded, setIsExpanded] = useState<boolean>(isActive);
  const [consultationDate, setConsultationDate] = useState<string>(
    toDateInputValue(stepData?.consultationData?.completedAt),
  );
  const [actionPlanFileName, setActionPlanFileName] = useState<string>('');
  const [sessionDocumentationFileName, setSessionDocumentationFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const actionPlanInputRef = useRef<HTMLInputElement>(null);
  const sessionDocumentationInputRef = useRef<HTMLInputElement>(null);

  const headerDate = formatDateForHeader(stepData);
  const headerLabel = isCompleted && headerDate
    ? `${translate('nutritionAdvice.consultation.consultationTitle')} ${stepNumber} - ${headerDate}`
    : `${translate('nutritionAdvice.consultation.consultationTitle')} ${stepNumber}`;

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  const onSelectFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFileName: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const selectedFile = event.target.files?.[0];
    setFileName(selectedFile?.name || '');
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!consultationDate) {
      errors.push(
        `${translate('nutritionAdvice.consultation.dateLabel')} ${translate('ra.validation.required')}`,
      );
    }

    if (!actionPlanInputRef.current?.files?.length && !existingActionPlanUrl) {
      errors.push(
        `${translate('nutritionAdvice.consultation.uploadActionPlanLabel')} ${translate('ra.validation.required')}`,
      );
    }

    if (!sessionDocumentationInputRef.current?.files?.length && !existingSessionDocumentationUrl) {
      errors.push(
        `${translate('nutritionAdvice.consultation.uploadSessionDocumentationLabel')} ${translate('ra.validation.required')}`,
      );
    }

    return errors;
  };

  const uploadDocumentIfPresent = async (
    fileRef: React.RefObject<HTMLInputElement>,
    documentType: string,
  ): Promise<string | undefined> => {
    if (!fileRef.current?.files?.length || !record?.id) {
      return undefined;
    }

    const selectedFile = fileRef.current.files[0];
    const response = await uploadFile(
      `treatments/${record.id}/upload-nutrition-advice-file`,
      selectedFile,
      { fileType: documentType },
    );

    return response.object;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (errors.length > 0 || !record?.id) {
      return;
    }

    try {
      setIsLoading(true);

      const actionPlanUrl = await uploadDocumentIfPresent(
        actionPlanInputRef,
        UPLOAD_TARGETS.actionPlan.documentType,
      );
      const sessionDocumentationUrl = await uploadDocumentIfPresent(
        sessionDocumentationInputRef,
        UPLOAD_TARGETS.sessionDocumentation.documentType,
      );

      await updateResource(`treatments/${record.id}`, {
        activeStepId: record?.activeStepId || stepData?.id,
        consultationDate,
        actionPlanUrl,
        sessionDocumentationUrl,
        type: 'nutritionAdvice',
      });

      notify('tests.addReportSuccessMessage', { type: 'success' });
      redirect('/treatments');
    } catch (error) {
      notify('tests.addReportErrorMessage', { type: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="nutritionAdviceConsultationForm__step">
      <div
        className="accordion-header"
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <span className="nutritionAdviceConsultationForm__stepTitle">{headerLabel}</span>
        <IconButton className="accordion-toggle-icon" size="small" aria-label="expand or collapse">
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>

      {isExpanded && (
        <div className="nutritionAdviceConsultationForm__stepContent">
          <div className="nutritionAdviceConsultationForm__row">
            <div className="nutritionAdviceConsultationForm__label">
              {translate('nutritionAdvice.consultation.dateLabel')}
            </div>
            <TextField
              type="date"
              value={consultationDate}
              onChange={(event) => setConsultationDate(event.target.value)}
              disabled={isCompleted}
              InputLabelProps={{ shrink: true }}
              className="nutritionAdviceConsultationForm__date"
            />
          </div>

          <div className="nutritionAdviceConsultationForm__row">
            <div className="nutritionAdviceConsultationForm__label">
              {translate(UPLOAD_TARGETS.actionPlan.labelKey)}
            </div>
            <div className="nutritionAdviceConsultationForm__controls">
              {isCompleted && !!existingActionPlanUrl && (
                <Link
                  href={existingActionPlanUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text--purple"
                >
                  {translate('nutritionAdvice.consultation.linkToActionPlan')}
                </Link>
              )}
              {!isCompleted && (
                <>
                  {!!actionPlanFileName && (
                    <div className="nutritionAdviceConsultationForm__fileName">
                      {actionPlanFileName}
                    </div>
                  )}
                  {!actionPlanFileName && !!existingActionPlanUrl && (
                    <Link
                      href={existingActionPlanUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text--purple"
                    >
                      {translate('nutritionAdvice.consultation.openUploadedFile')}
                    </Link>
                  )}
                  <input
                    ref={actionPlanInputRef}
                    type="file"
                    accept={FILE_ACCEPT}
                    onChange={(event) => onSelectFile(event, setActionPlanFileName)}
                    className="nutritionAdviceConsultationForm__hiddenInput"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => actionPlanInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    {translate('nutritionAdvice.consultation.uploadButton')}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="nutritionAdviceConsultationForm__row">
            <div className="nutritionAdviceConsultationForm__label">
              {translate(UPLOAD_TARGETS.sessionDocumentation.labelKey)}
            </div>
            <div className="nutritionAdviceConsultationForm__controls">
              {isCompleted && !!existingSessionDocumentationUrl && (
                <Link
                  href={existingSessionDocumentationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text--purple"
                >
                  {translate('nutritionAdvice.consultation.linkToSessionDocumentation')}
                </Link>
              )}
              {!isCompleted && (
                <>
                  {!!sessionDocumentationFileName && (
                    <div className="nutritionAdviceConsultationForm__fileName">
                      {sessionDocumentationFileName}
                    </div>
                  )}
                  {!sessionDocumentationFileName && !!existingSessionDocumentationUrl && (
                    <Link
                      href={existingSessionDocumentationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text--purple"
                    >
                      {translate('nutritionAdvice.consultation.openUploadedFile')}
                    </Link>
                  )}
                  <input
                    ref={sessionDocumentationInputRef}
                    type="file"
                    accept={FILE_ACCEPT}
                    onChange={(event) => onSelectFile(event, setSessionDocumentationFileName)}
                    className="nutritionAdviceConsultationForm__hiddenInput"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => sessionDocumentationInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    {translate('nutritionAdvice.consultation.uploadButton')}
                  </Button>
                </>
              )}
            </div>
          </div>

          {formErrors.length > 0 && (
            <div className="formErrors">
              <ul>
                {formErrors.map((error) => (
                  <li key={error}>* {error}</li>
                ))}
              </ul>
            </div>
          )}

          {isActive && (
            <div className="nutritionAdviceConsultationForm__submit">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24.5} />
                ) : (
                  translate('nutritionAdvice.consultation.submitButton')
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsultationStepSection;
